import { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Divider,
  Dropdown,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  SegmentedControl,
  SegmentedControlItem,
  Select,
  SelectOption,
  Spacer,
  Spinner,
  Stack,
  Tag,
  TableCell,
  TextBody,
  Toggle,
} from '@pypestream/design-system';
import { ProductName } from '@pypestream/api-services';
import { sortBy, uniqBy } from 'lodash';
import { RenderViaTeamDetails } from '../render-via-team-details';
import { sendManagerEvent } from '../../../xstate/app.xstate';
import { AnalyticsRoleNames } from '../../../xstate/permission-details-xstate-helper';
import {
  AccountProject,
  ProjectProductRoles,
} from '../permissions-table.types';
import {
  MetabaseAccessScope,
  MetabaseDataset,
  MetabaseFoldersByDataset,
  METABASE_DATASETS,
  METABASE_DATASET_LABELS,
  scopeToRows,
  distinctScopeRoleIds,
  scopeToMetabaseAccessPayload,
  scopeFromMetabaseAccess,
} from './metabase-access.types';
import { fetchMetabaseFolders } from './metabase-access-helper';
import {
  ConfirmMetabaseUpgradeModal,
  ConfirmMetabaseUpgradeVariant,
} from './confirm-metabase-upgrade-modal';

export type MetabaseAccessCellProps = {
  project: AccountProject;
  productId: string;
  canManageAccess: boolean;
  isSavingDetails: boolean;
  isPypestreamDataTeamEligible: boolean;
};

type AnalyticsRole = NonNullable<ProjectProductRoles>[number];

const buildRoleParam = (role: {
  id: string;
  name?: string;
  productId?: string;
  product?: { id?: string; name?: string; displayName?: string };
}): AnalyticsRole => ({
  id: role.id,
  name: role.name || '',
  productId: role.productId || '',
  product: {
    id: role.product?.id || role.productId || '',
    name: (role.product?.name || '') as ProductName,
    displayName: role.product?.displayName || '',
  },
});

const DATASET_TAG_VARIANT: Record<MetabaseDataset, 'warning' | 'success'> = {
  sandbox: 'warning',
  live: 'success',
};

const DatasetBadge: FC<{ dataset: MetabaseDataset }> = ({ dataset }) => (
  <Tag variant={DATASET_TAG_VARIANT[dataset]}>
    {METABASE_DATASET_LABELS[dataset]}
  </Tag>
);

type AddDashboardGroupPanelProps = {
  roleOptions: AnalyticsRole[];
  groupsByDataset: MetabaseFoldersByDataset;
  existingScope: MetabaseAccessScope;
  fullAccess: boolean;
  onFullAccessChange: (next: boolean) => void;
  isLoadingFolders: boolean;
  isSaving: boolean;
  onSave: (
    selections: Record<MetabaseDataset, string[]>,
    roleId: string
  ) => void;
  onSaveFullAccess: (roleId: string) => void;
  onCancel: () => void;
};

const AddDashboardGroupPanel: FC<AddDashboardGroupPanelProps> = ({
  roleOptions,
  groupsByDataset,
  existingScope,
  fullAccess,
  onFullAccessChange,
  isLoadingFolders,
  isSaving,
  onSave,
  onSaveFullAccess,
  onCancel,
}) => {
  const [roleId, setRoleId] = useState<string>(roleOptions[0]?.id || '');
  const [activeDataset, setActiveDataset] = useState<MetabaseDataset>(
    METABASE_DATASETS[0]
  );
  const [selected, setSelected] = useState<Record<MetabaseDataset, string[]>>({
    live: [],
    sandbox: [],
  });

  const groups = groupsByDataset[activeDataset] || [];
  const activeSelection = selected[activeDataset];
  const allSelected =
    groups.length > 0 && activeSelection.length === groups.length;

  const toggleGroup = (groupId: string) => {
    setSelected((prev) => {
      const current = prev[activeDataset];
      const next = current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId];
      return { ...prev, [activeDataset]: next };
    });
  };

  const toggleAll = () => {
    setSelected((prev) => ({
      ...prev,
      [activeDataset]: allSelected ? [] : groups.map((group) => group.id),
    }));
  };

  const roleNameById = (id: string) =>
    roleOptions.find((role) => role.id === id)?.name || '';

  const hasSelection = selected.live.length > 0 || selected.sandbox.length > 0;
  const canSave = !!roleId && (fullAccess || hasSelection);

  return (
    <div className="c-metabase-access">
      <TextBody size="small">Role</TextBody>
      <Spacer size="2xsmall" />
      <Select
        variant="outlined"
        value={roleId}
        disabled={isSaving || !roleOptions.length}
        onChange={(e) => {
          const nextId = (e.target as unknown as { value: string }).value;
          if (nextId) {
            setRoleId(nextId);
          }
        }}
      >
        {roleOptions.map((role) => (
          <SelectOption key={role.id} value={role.id}>
            {role.name}
          </SelectOption>
        ))}
      </Select>

      <Spacer size="medium" />
      <Stack
        justifyContent="space-between"
        alignItems="center"
        gutter="small"
        nowrap
      >
        <div>
          <TextBody size="small">Full access</TextBody>
          <TextBody size="small" variant="secondary">
            Grants access to all Sandbox and Live Dashboard Groups, including
            the ones added later.
          </TextBody>
        </div>
        <Toggle
          checked={fullAccess}
          disabled={isSaving}
          onChange={(e) => onFullAccessChange(e.target.checked)}
        />
      </Stack>

      {!fullAccess && (
        <>
          <Spacer size="medium" />
          <SegmentedControl
            value={activeDataset}
            onChange={(e) =>
              setActiveDataset(e.detail.value as MetabaseDataset)
            }
          >
            {METABASE_DATASETS.map((dataset) => (
              <SegmentedControlItem key={dataset} value={dataset}>
                <Tag
                  variant={DATASET_TAG_VARIANT[dataset]}
                  new-variant="ghost"
                  size="medium"
                >
                  {METABASE_DATASET_LABELS[dataset]}
                </Tag>
              </SegmentedControlItem>
            ))}
          </SegmentedControl>

          <Spacer size="small" />
          <Divider />
          <Spacer size="small" />

          {isLoadingFolders ? (
            <Stack
              className="c-metabase-access__groups-placeholder"
              direction="column"
              alignItems="center"
              justifyContent="center"
            >
              <Spinner />
            </Stack>
          ) : (
            <div className="c-metabase-access__groups">
              {groups.length > 0 && (
                <label className="c-metabase-access__group-row">
                  <Stack alignItems="center" gutter="xsmall" nowrap>
                    <Checkbox
                      checked={allSelected}
                      disabled={isSaving}
                      onChange={toggleAll}
                    />
                    <TextBody>Select all</TextBody>
                  </Stack>
                </label>
              )}
              {groups.map((group) => {
                const checked = activeSelection.includes(group.id);
                const existingRoleId =
                  existingScope[activeDataset].folders[group.id];
                return (
                  <label
                    key={group.id}
                    className="c-metabase-access__group-row"
                  >
                    <Stack alignItems="center" gutter="xsmall" nowrap>
                      <Checkbox
                        checked={checked}
                        disabled={isSaving}
                        onChange={() => toggleGroup(group.id)}
                      />
                      <TextBody>{group.name}</TextBody>
                    </Stack>
                    {existingRoleId && (
                      <TextBody size="small" variant="secondary">
                        {roleNameById(existingRoleId)}
                      </TextBody>
                    )}
                  </label>
                );
              })}
              {!groups.length && (
                <TextBody size="small" variant="secondary">
                  No dashboard groups in this dataset.
                </TextBody>
              )}
            </div>
          )}
        </>
      )}

      <Spacer size="small" />
      <Divider />
      <Spacer size="small" />
      <Stack justifyContent="end" gutter="small">
        <Button
          variant="secondary"
          size="medium"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          loading={isSaving}
          disabled={!canSave}
          onClick={() =>
            fullAccess ? onSaveFullAccess(roleId) : onSave(selected, roleId)
          }
        >
          Save
        </Button>
      </Stack>
    </div>
  );
};

export const MetabaseAccessCell: FC<MetabaseAccessCellProps> = ({
  project,
  productId,
  canManageAccess,
  isSavingDetails,
  isPypestreamDataTeamEligible,
}) => {
  const { user_id: userId = '' } = useParams();

  const projectId = project.project.id;
  const orgId = project.project.accountId;

  const productRoles = project._account_project_roles.filter(
    (accountProjectRole) => accountProjectRole.role.productId === productId
  );
  const directRoles = productRoles.filter(
    (accountProjectRole) => !accountProjectRole.teams
  );
  const inheritedRoles = productRoles.filter(
    (accountProjectRole) =>
      accountProjectRole.teams && accountProjectRole.teams.length > 0
  );

  const availableProductRoles = useMemo(
    () =>
      sortBy(
        project.availableProjectRoles?.filter(
          (role) =>
            role.productId === productId &&
            role.name !== AnalyticsRoleNames.ANALYTICS_MEMBER &&
            // Only Pypestream employees may be granted this role.
            (role.name !== AnalyticsRoleNames.PYPESTREAM_DATA_TEAM ||
              isPypestreamDataTeamEligible)
        ),
        'name'
      ),
    [project.availableProjectRoles, productId, isPypestreamDataTeamEligible]
  );

  const roleOptions = useMemo(
    () =>
      sortBy(
        uniqBy(
          [
            ...availableProductRoles,
            ...directRoles.map((accountProjectRole) =>
              buildRoleParam(accountProjectRole.role)
            ),
          ],
          'id'
        ),
        'name'
      ).filter(
        (role) =>
          role.name !== AnalyticsRoleNames.ANALYTICS_MEMBER &&
          // directRoles can carry an already-assigned Pypestream Data Team grant back in even
          // after the target stops being eligible (e.g. a non-employee joins the team) — strip it
          // here too so it can't be extended to more folders via AddDashboardGroupPanel.
          (role.name !== AnalyticsRoleNames.PYPESTREAM_DATA_TEAM ||
            isPypestreamDataTeamEligible)
      ),
    [availableProductRoles, directRoles, isPypestreamDataTeamEligible]
  );

  const roleById = useMemo(() => {
    const map = new Map<string, AnalyticsRole>();
    roleOptions.forEach((role) => map.set(role.id || '', role));
    return map;
  }, [roleOptions]);

  const roleName = (roleId: string) => roleById.get(roleId)?.name || '';

  const scope = useMemo(
    () => scopeFromMetabaseAccess(project.metabase_access),
    [project.metabase_access]
  );

  const grantRows = useMemo(() => scopeToRows(scope), [scope]);
  const hasGrants = grantRows.length > 0;
  const wholeProjectRole = !hasGrants ? directRoles[0]?.role : undefined;

  const [open, setOpen] = useState(false);
  const [folders, setFolders] = useState<MetabaseFoldersByDataset | null>(null);
  const [isLoadingFolders, setIsLoadingFolders] = useState(false);
  const [foldersError, setFoldersError] = useState(false);
  const [fullAccess, setFullAccess] = useState(false);

  useEffect(() => {
    if (open) {
      setFullAccess(!!wholeProjectRole || (!hasGrants && !wholeProjectRole));
      setFoldersError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const needFolders = hasGrants || (open && !fullAccess);
    if (!needFolders || folders || isLoadingFolders || foldersError) {
      return;
    }
    setIsLoadingFolders(true);
    fetchMetabaseFolders(projectId)
      .then((result) => {
        setFolders(result);
        setFoldersError(false);
      })
      .catch(() => setFoldersError(true))
      .finally(() => setIsLoadingFolders(false));
  }, [
    open,
    fullAccess,
    hasGrants,
    folders,
    isLoadingFolders,
    foldersError,
    projectId,
  ]);

  const folderName = (dataset: MetabaseDataset, groupId: string) =>
    (folders?.[dataset] || []).find((group) => group.id === groupId)?.name ||
    `Folder ${groupId}`;

  const resolveDesiredRoles = (roleIds: string[]): AnalyticsRole[] =>
    roleIds.map(
      (roleId) =>
        roleById.get(roleId) || buildRoleParam({ id: roleId, productId })
    );

  const saveScope = (nextScope: MetabaseAccessScope) => {
    if (!userId) {
      return;
    }
    sendManagerEvent({
      type: 'manager.userDetails.updateProjectMetabaseAccess',
      orgId,
      projectId,
      productId,
      metabaseAccess: scopeToMetabaseAccessPayload(nextScope),
      desiredRoles: resolveDesiredRoles(distinctScopeRoleIds(nextScope)),
    });
  };

  const saveWholeProject = (role: AnalyticsRole | null) => {
    if (!userId) {
      return;
    }
    sendManagerEvent({
      type: 'manager.userDetails.updateProjectMetabaseAccess',
      orgId,
      projectId,
      productId,
      metabaseAccess: [],
      desiredRoles: role ? [role] : [],
    });
  };

  const [pendingAdmin, setPendingAdmin] = useState<{
    variant: ConfirmMetabaseUpgradeVariant;
    role: AnalyticsRole;
    currentRoleName?: string;
  } | null>(null);

  const isAnalyticsAdmin = (role?: { name?: string } | null) =>
    role?.name === AnalyticsRoleNames.ANALYTICS_ADMIN;

  const requestWholeProject = (role: AnalyticsRole | null) => {
    const isFirstAssignment = !hasGrants && !wholeProjectRole;
    if (
      !isAnalyticsAdmin(role) ||
      isAnalyticsAdmin(wholeProjectRole) ||
      isFirstAssignment
    ) {
      saveWholeProject(role);
      return;
    }
    if (hasGrants) {
      const scopedRoleIds = distinctScopeRoleIds(scope);
      setPendingAdmin({
        variant: 'fullAccess',
        role: role!,
        currentRoleName:
          scopedRoleIds.length === 1 ? roleName(scopedRoleIds[0]) : undefined,
      });
    } else {
      setPendingAdmin({
        variant: 'upgrade',
        role: role!,
        currentRoleName: wholeProjectRole?.name,
      });
    }
  };

  const confirmPendingAdmin = () => {
    if (!pendingAdmin) {
      return;
    }
    saveWholeProject(pendingAdmin.role);
    setPendingAdmin(null);
  };

  const cloneScope = (): MetabaseAccessScope =>
    METABASE_DATASETS.reduce((acc, ds) => {
      acc[ds] = { folders: { ...scope[ds].folders } };
      return acc;
    }, {} as MetabaseAccessScope);

  const changeGrantRole = (
    dataset: MetabaseDataset,
    groupId: string,
    roleId: string
  ) => {
    const next = cloneScope();
    next[dataset].folders[groupId] = roleId;
    saveScope(next);
  };

  const removeGrant = (dataset: MetabaseDataset, groupId: string) => {
    const next = cloneScope();
    delete next[dataset].folders[groupId];
    saveScope(next);
  };

  const handleAddGroups = (
    selections: Record<MetabaseDataset, string[]>,
    roleId: string
  ) => {
    const next = cloneScope();
    METABASE_DATASETS.forEach((ds) => {
      selections[ds].forEach((groupId) => {
        next[ds].folders[groupId] = roleId;
      });
    });
    saveScope(next);
    setOpen(false);
  };

  const groupsByDataset = folders ?? { live: [], sandbox: [] };
  const addTriggerId = `${projectId}-${productId}-metabase-add-group`;
  const popoverExtraProps = {
    onClose: () => setOpen(false),
  } as Record<string, unknown>;

  const isNoAccess = !hasGrants && !wholeProjectRole;

  const addDashboardGroupPopover = canManageAccess && (
    <Popover
      {...popoverExtraProps}
      trigger={addTriggerId}
      active={open}
      width="auto"
      disableTriggerListeners
      closeOnClickOutside
      stayOpenOnSelect
      placement="bottom-start"
    >
      {open && (
        <AddDashboardGroupPanel
          roleOptions={roleOptions}
          groupsByDataset={groupsByDataset}
          existingScope={scope}
          fullAccess={fullAccess}
          onFullAccessChange={setFullAccess}
          isLoadingFolders={isLoadingFolders}
          isSaving={isSavingDetails}
          onCancel={() => setOpen(false)}
          onSave={handleAddGroups}
          onSaveFullAccess={(rid) => {
            setOpen(false);
            requestWholeProject(
              roleById.get(rid) || buildRoleParam({ id: rid, productId })
            );
          }}
        />
      )}
    </Popover>
  );

  const addDashboardGroupButton = canManageAccess && !isNoAccess && (
    <Button
      variant="ghost"
      size="small"
      id={addTriggerId}
      disabled={isSavingDetails || !roleOptions.length}
      onClick={() => setOpen((prev) => !prev)}
    >
      <Icon name="add" slot="prefix" />
      Add dashboard group
    </Button>
  );

  return (
    <TableCell>
      <Stack alignItems="start" gutter="xsmall" direction="column">
        {hasGrants &&
          grantRows.map((row, index) => {
            const roleTriggerId = `${projectId}-${productId}-${row.dataset}-${row.groupId}-role`;
            const canEditRole = canManageAccess && !isSavingDetails;
            return (
              <Fragment key={`${row.dataset}-${row.groupId}`}>
                {index > 0 && <Divider variant="dashed" />}
                <Stack
                  className="c-metabase-access__grant-row"
                  alignItems="center"
                  gutter="xsmall"
                  nowrap
                >
                  {folders || foldersError ? (
                    <TextBody size="small">
                      {folderName(row.dataset, row.groupId)}
                    </TextBody>
                  ) : (
                    <span
                      className="c-metabase-access__name-skeleton"
                      aria-hidden
                    />
                  )}
                  <DatasetBadge dataset={row.dataset} />
                  <TextBody size="small" variant="secondary">
                    :
                  </TextBody>
                  <Tag
                    minimal
                    new-variant="outlined"
                    removable={canManageAccess}
                    disabled={isSavingDetails}
                    {...(canEditRole && {
                      id: roleTriggerId,
                      interactive: true,
                    })}
                    onRemove={() => removeGrant(row.dataset, row.groupId)}
                  >
                    <Icon name="users" />
                    {roleName(row.roleId)}
                    {canEditRole && <Icon name="chevron-down" />}
                    {canEditRole && (
                      <Dropdown
                        trigger={roleTriggerId}
                        placement="bottom-start"
                        closeOnClickOutside
                      >
                        <Menu>
                          {roleOptions
                            .filter((role) => role.id !== row.roleId)
                            .map((role) => (
                              <MenuItem
                                key={role.id}
                                disabled={isSavingDetails}
                                onClick={(e) => {
                                  e.preventDefault();
                                  changeGrantRole(
                                    row.dataset,
                                    row.groupId,
                                    role.id || ''
                                  );
                                }}
                              >
                                {role.name}
                              </MenuItem>
                            ))}
                        </Menu>
                      </Dropdown>
                    )}
                  </Tag>
                </Stack>
              </Fragment>
            );
          })}

        {wholeProjectRole && (
          <Stack
            className="c-metabase-access__grant-row"
            alignItems="center"
            gutter="xsmall"
            nowrap
          >
            <TextBody size="small">Full access</TextBody>
            <DatasetBadge dataset="live" />
            <DatasetBadge dataset="sandbox" />
            <TextBody size="small" variant="secondary">
              :
            </TextBody>
            <Tag
              minimal
              new-variant="outlined"
              removable={canManageAccess}
              disabled={isSavingDetails}
              {...(canManageAccess &&
                !isSavingDetails && {
                  id: `${projectId}-${productId}-metabase-whole`,
                  interactive: true,
                })}
              onRemove={() => saveWholeProject(null)}
            >
              <Icon name="users" />
              {wholeProjectRole.name}
              {canManageAccess && <Icon name="chevron-down" />}
              {canManageAccess && !isSavingDetails && (
                <Dropdown
                  trigger={`${projectId}-${productId}-metabase-whole`}
                  placement="bottom-start"
                  closeOnClickOutside
                >
                  <Menu>
                    {roleOptions
                      .filter((role) => role.id !== wholeProjectRole.id)
                      .map((role) => (
                        <MenuItem
                          key={role.id}
                          disabled={isSavingDetails}
                          onClick={(e) => {
                            e.preventDefault();
                            requestWholeProject(role);
                          }}
                        >
                          {role.name}
                        </MenuItem>
                      ))}
                  </Menu>
                </Dropdown>
              )}
            </Tag>
          </Stack>
        )}

        {inheritedRoles.map((inheritedRole) => (
          <Tag
            minimal
            new-variant="outlined"
            key={`${projectId}-${productId}-${inheritedRole.role.id}-inherited`}
            disabled={isSavingDetails}
          >
            <Icon name="teams" />
            {inheritedRole.role.name}
            <RenderViaTeamDetails teams={inheritedRole.teams || []} />
          </Tag>
        ))}

        {canManageAccess && isNoAccess && (
          <Stack alignItems="center" gutter="2xsmall">
            <IconButton
              name="add"
              id={addTriggerId}
              disabled={isSavingDetails || !roleOptions.length}
              onClick={() => setOpen((prev) => !prev)}
            />
            {!inheritedRoles.length && (
              <TextBody variant="secondary" size="small">
                No access — assign a role
              </TextBody>
            )}
          </Stack>
        )}

        {addDashboardGroupButton}
      </Stack>
      {addDashboardGroupPopover}

      <ConfirmMetabaseUpgradeModal
        open={!!pendingAdmin}
        variant={pendingAdmin?.variant ?? 'upgrade'}
        projectName={project.project.name}
        targetRoleName={pendingAdmin?.role.name || ''}
        currentRoleName={pendingAdmin?.currentRoleName}
        isSaving={isSavingDetails}
        onCancel={() => setPendingAdmin(null)}
        onConfirm={confirmPendingAdmin}
      />
    </TableCell>
  );
};
